// ============================================
// APP: NVA (React App)
// FILE: src/components/JotFormChatModal.tsx
// PURPOSE: Modal overlay for JotForm AI chatbot
// ============================================

import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X, Loader2 } from "lucide-react";
import "./JotFormChatModal.css";

interface JotFormChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const JotFormChatModal: React.FC<JotFormChatModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const scriptLoaded = useRef(false);

  // Track if modal has ever been opened (to mount iframe only once needed)
  useEffect(() => {
    if (isOpen && !hasBeenOpened) {
      setHasBeenOpened(true);
    }
  }, [isOpen, hasBeenOpened]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, onClose]);

  // Load JotForm embed handler script (only once)
  useEffect(() => {
    if (hasBeenOpened && !scriptLoaded.current) {
      scriptLoaded.current = true;
      const existingScript = document.querySelector('script[src*="jotfor.ms"]');
      if (!existingScript) {
        const script = document.createElement("script");
        script.src = "https://cdn.jotfor.ms/s/umd/f871828fd7d/for-form-embed-handler.js";
        script.async = true;
        document.body.appendChild(script);

        script.onload = () => {
          if ((window as any).jotformEmbedHandler) {
            (window as any).jotformEmbedHandler(
              "iframe[id='JotFormIFrame-019b800791da7578b0a2bd33fbb366c8b567']",
              "https://eu.jotform.com"
            );
          }
        };
      } else {
        if ((window as any).jotformEmbedHandler) {
          (window as any).jotformEmbedHandler(
            "iframe[id='JotFormIFrame-019b800791da7578b0a2bd33fbb366c8b567']",
            "https://eu.jotform.com"
          );
        }
      }
    }
  }, [hasBeenOpened]);

  // Don't render anything until the modal has been opened at least once
  if (!hasBeenOpened) return null;

  return createPortal(
    <div
      className={`jotform-modal-overlay ${isOpen ? '' : 'jotform-modal-hidden'}`}
      onClick={onClose}
    >
      <div className="jotform-modal-container" onClick={(e) => e.stopPropagation()}>
        <button className="jotform-modal-close" onClick={onClose} aria-label={t('jotformChat.closeAriaLabel')}>
          <X size={24} />
        </button>
        <div className="jotform-modal-content">
          {/* Loading spinner - shown until iframe loads */}
          {!iframeLoaded && (
            <div className="jotform-loading">
              <Loader2 className="jotform-loading-spinner" size={40} />
              <p className="jotform-loading-text">{t('jotformChat.loading')}</p>
            </div>
          )}
          <iframe
            id="JotFormIFrame-019b800791da7578b0a2bd33fbb366c8b567"
            title={t('jotformChat.iframeTitle')}
            allow="geolocation; microphone; camera; fullscreen"
            src="https://eu.jotform.com/agent/019b800791da7578b0a2bd33fbb366c8b567?embedMode=iframe&background=1&shadow=1"
            frameBorder="0"
            scrolling="no"
            className="jotform-iframe"
            onLoad={() => setIframeLoaded(true)}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default JotFormChatModal;
